/**
 * DesktopDevices — "License & devices" sub-view for the desktop Account screen.
 *
 * Rendered at /account/devices. Shows:
 *   - License status (activated / free)
 *   - Active sessions list with per-session sign-out
 *   - Deactivate this device button (licensed only)
 *   - Activate / Buy affordance (free tier)
 *
 * Logic ported from Account.jsx's sessions + offline-license sections.
 */
import { useState, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Monitor, Globe, Apple, Loader2, LogOut,
  ShieldCheck, BadgeCheck, CheckCircle,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useBilling } from '@/hooks/useBilling';
import { getIdToken } from '@/lib/auth';
import { verifyStoredLicense, clearLicense } from '@/lib/licenseToken';
import { openExternalUrl, SCRIVAR_BILLING_URL } from '@/lib/external';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(date) {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return date.toLocaleDateString();
}

function platformLabel(platform) {
  if (platform === 'web') return 'Web browser';
  if (platform === 'mac') return 'macOS desktop';
  if (platform === 'win') return 'Windows desktop';
  return platform;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SessionRow({ session, onSignOut, signingOut }) {
  const Icon = session.platform === 'web' ? Globe
    : session.platform === 'mac' ? Apple
    : Monitor;
  const label = platformLabel(session.platform);
  const lastActive = session.last_active
    ? formatRelativeTime(new Date(session.last_active))
    : '—';
  const cached = (() => {
    try { return JSON.parse(localStorage.getItem('scrivar_session') || 'null'); } catch { return null; }
  })();
  const isCurrent = cached?.token && session.session_token && cached.token === session.session_token;

  return (
    <li style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '12px 16px',
      borderBottom: '1px solid var(--ds-border, #EAEBF0)',
    }}>
      <Icon size={16} color="var(--ds-ink-3, #8C8FA0)" aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '13.5px', fontWeight: 600,
          color: 'var(--ds-ink, #191A24)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {label}
          {isCurrent && (
            <span style={{
              fontSize: '10px', fontWeight: 600, letterSpacing: '0.05em',
              textTransform: 'uppercase', color: 'var(--ds-indigo, #4F46E5)',
              background: 'rgba(var(--ds-indigo-rgb, 79,70,229), .08)', borderRadius: '4px',
              padding: '1px 6px',
            }}>
              This device
            </span>
          )}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--ds-ink-3, #8C8FA0)' }}>
          Last active {lastActive}
        </div>
      </div>
      <button
        type="button"
        onClick={onSignOut}
        disabled={signingOut}
        aria-label={`Sign out of ${label}`}
        style={{
          height: '32px', padding: '0 12px',
          borderRadius: '8px',
          background: 'var(--ds-surface, #fff)',
          border: '1px solid var(--ds-border-2, #D4D7E3)',
          fontFamily: 'inherit', fontSize: '12px', fontWeight: 600,
          color: 'var(--ds-ink-2, #565869)', cursor: signingOut ? 'default' : 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          opacity: signingOut ? 0.5 : 1,
        }}
      >
        {signingOut
          ? <Loader2 size={13} aria-hidden="true" />
          : <LogOut size={13} aria-hidden="true" />
        }
        Sign out
      </button>
    </li>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DesktopDevices() {
  const navigate = useNavigate();
  const { account, refreshAccount } = useAuth();
  const { tier, hasLifetime } = useBilling();

  const [offlineLicense, setOfflineLicense] = useState(null);
  const [deactivateState, setDeactivateState] = useState('idle'); // idle|confirm|busy|error
  const [sessionSigningOut, setSessionSigningOut] = useState({});
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    verifyStoredLicense().then((s) => setOfflineLicense(s.valid ? s.claims : null));
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function killSession(sessionId) {
    setSessionSigningOut((s) => ({ ...s, [sessionId]: true }));
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not sign out that session.');
      setToast('Signed out of that device.');
      await refreshAccount();
    } catch (err) {
      setError(err.message);
    } finally {
      setSessionSigningOut((s) => {
        const next = { ...s };
        delete next[sessionId];
        return next;
      });
    }
  }

  async function deactivateThisDevice() {
    setDeactivateState('busy');
    try {
      const fingerprint = await window.electronAPI?.getDeviceFingerprint?.();
      if (!fingerprint) throw new Error('Could not read this device.');
      const token = await getIdToken();
      const res = await fetch(`${API}/license/deactivate-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deviceFingerprint: fingerprint }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Could not deactivate this device.');
      }
      await clearLicense();
      setOfflineLicense(null);
      setDeactivateState('idle');
      setToast('Device deactivated. Sign in on another computer to use your license there.');
    } catch (err) {
      console.error('deactivate-device failed:', err);
      setDeactivateState('error');
    }
  }

  // ── Derived state ───────────────────────────────────────────────────────────
  const sessions = account?.sessions || [];
  const isLicensed = hasLifetime || !!offlineLicense;
  const tierLabel = tier === 'lifetime_pro' ? 'Pro'
    : tier === 'lifetime_lite' ? 'Lite'
    : null;
  const offlineTierLabel = offlineLicense
    ? (offlineLicense.tier === 'pro' ? 'Pro' : 'Lite')
    : null;
  const displayTier = offlineTierLabel || tierLabel;
  const transfersRemaining = offlineLicense?.transfersRemaining ?? null;

  // ── Shared styles ───────────────────────────────────────────────────────────
  const card = {
    background: 'var(--ds-surface, #fff)',
    border: '1px solid var(--ds-border, #EAEBF0)',
    borderRadius: 'var(--ds-radius, 12px)',
    boxShadow: 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))',
    overflow: 'hidden',
    marginBottom: '16px',
  };
  const sectionHead = {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '14px 16px',
    borderBottom: '1px solid var(--ds-border, #EAEBF0)',
    background: 'var(--ds-sidebar, #F4F5F8)',
  };
  const sectionTitleStyle = {
    fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em',
    textTransform: 'uppercase', color: 'var(--ds-ink-2, #565869)',
    margin: 0,
  };

  return (
    <div style={{ padding: '24px', maxWidth: '620px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 4px', color: 'var(--ds-ink, #191A24)' }}>
          License &amp; devices
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--ds-ink-2, #565869)', margin: 0 }}>
          Your license status and active sign-ins.
        </p>
      </div>

      {error && (
        <p role="alert" style={{ fontSize: '13px', color: 'var(--ds-red, #B42318)', marginBottom: '12px' }}>
          {error}
        </p>
      )}

      {/* ── License card ──────────────────────────────────────────────── */}
      <div style={card}>
        <div style={sectionHead}>
          <ShieldCheck size={14} color="var(--ds-ink-3, #8C8FA0)" aria-hidden="true" />
          <h3 style={sectionTitleStyle}>License</h3>
        </div>

        <div style={{ padding: '16px 20px' }}>
          {isLicensed && displayTier ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <BadgeCheck size={16} color="var(--ds-green-bright, #16A34A)" aria-hidden="true" />
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ds-ink, #191A24)' }}>
                  {displayTier} lifetime &middot; activated on this device
                </span>
              </div>
              {transfersRemaining != null && (
                <p style={{ fontSize: '12px', color: 'var(--ds-ink-3, #8C8FA0)', margin: '0 0 12px' }}>
                  {transfersRemaining} transfer{transfersRemaining === 1 ? '' : 's'} remaining
                </p>
              )}
              <p style={{ fontSize: '12px', color: 'var(--ds-ink-3, #8C8FA0)', margin: '0 0 12px' }}>
                Works offline indefinitely — no internet check-in is required to stay activated.
              </p>

              {deactivateState === 'error' && (
                <p role="alert" style={{ fontSize: '13px', color: 'var(--ds-red, #B42318)', marginBottom: '10px' }}>
                  Could not deactivate this device. Check your connection and try again.
                </p>
              )}

              {deactivateState === 'confirm' ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={deactivateThisDevice}
                    style={{
                      height: '34px', padding: '0 14px',
                      borderRadius: '8px', border: 'none',
                      background: 'var(--ds-red, #B42318)', color: 'var(--ds-content, #fff)',
                      fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Yes, deactivate
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeactivateState('idle')}
                    style={{
                      height: '34px', padding: '0 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--ds-border-2, #D4D7E3)',
                      background: 'var(--ds-surface, #fff)',
                      fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                      color: 'var(--ds-ink-2, #565869)', cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={deactivateState === 'busy'}
                  onClick={() => setDeactivateState('confirm')}
                  style={{
                    height: '34px', padding: '0 14px',
                    borderRadius: '8px',
                    border: '1px solid var(--ds-red-line, #F4C7C3)',
                    background: 'var(--ds-surface, #fff)',
                    fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                    color: 'var(--ds-red, #B42318)', cursor: deactivateState === 'busy' ? 'default' : 'pointer',
                    opacity: deactivateState === 'busy' ? 0.5 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  {deactivateState === 'busy' ? (
                    <><Loader2 size={13} aria-hidden="true" /> Deactivating&hellip;</>
                  ) : 'Deactivate this device'}
                </button>
              )}
            </>
          ) : (
            <>
              <p style={{ fontSize: '14px', color: 'var(--ds-ink-2, #565869)', margin: '0 0 4px', fontWeight: 500 }}>
                No license on this device
              </p>
              <p style={{ fontSize: '12px', color: 'var(--ds-ink-3, #8C8FA0)', margin: '0 0 14px' }}>
                The desktop app runs in trial mode without a lifetime license. Activate a code to unlock all features permanently.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => navigate('/activate')}
                  style={{
                    height: '34px', padding: '0 14px',
                    borderRadius: '8px', border: 'none',
                    background: 'var(--ds-indigo, #4F46E5)', color: 'var(--ds-content, #fff)',
                    fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Activate a license
                </button>
                <button
                  type="button"
                  onClick={() => openExternalUrl(SCRIVAR_BILLING_URL)}
                  style={{
                    height: '34px', padding: '0 14px',
                    borderRadius: '8px',
                    border: '1px solid var(--ds-border-2, #D4D7E3)',
                    background: 'var(--ds-surface, #fff)',
                    fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                    color: 'var(--ds-ink-2, #565869)', cursor: 'pointer',
                  }}
                >
                  Buy a license
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Sessions card ─────────────────────────────────────────────── */}
      <div style={card}>
        <div style={sectionHead}>
          <Monitor size={14} color="var(--ds-ink-3, #8C8FA0)" aria-hidden="true" />
          <h3 style={sectionTitleStyle}>Active sessions</h3>
        </div>

        {sessions.length > 0 ? (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Active sessions">
            {sessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                onSignOut={() => killSession(s.id)}
                signingOut={!!sessionSigningOut[s.id]}
              />
            ))}
          </ul>
        ) : (
          <p style={{ padding: '16px 20px', fontSize: '13px', color: 'var(--ds-ink-3, #8C8FA0)', margin: 0 }}>
            No active sessions.
          </p>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
            background: 'var(--ds-green-bright, #16A34A)', color: 'var(--ds-content, #fff)',
            padding: '10px 16px', borderRadius: '8px',
            fontSize: '13px', fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: '7px',
            boxShadow: 'var(--ds-shadow-elevated, 0 4px 12px rgba(0,0,0,.15))',
            zIndex: 50,
          }}
        >
          <CheckCircle size={15} aria-hidden="true" />
          {toast}
        </div>
      )}
    </div>
  );
}
